import { useCallback, useEffect, useState } from "react";
import type { ProjectStepId } from "config/navigation";

export interface SingleOpenStepState {
  /** The one step whose pages are listed. */
  openStepId: ProjectStepId | null;
  /** The locked step whose Studio explanation is showing. */
  upsellStepId: ProjectStepId | null;
  toggleStep: (stepId: ProjectStepId) => void;
}

/**
 * Only one step is open at a time. Opening a step closes the other one, and
 * landing on a page opens that page's step. A locked step shows its Studio
 * explanation instead of opening.
 */
export function useSingleOpenStep(
  activeStepId: ProjectStepId | null,
  isLocked: (stepId: ProjectStepId) => boolean
): SingleOpenStepState {
  const [openStepId, setOpenStepId] = useState<ProjectStepId | null>(() =>
    activeStepId && !isLocked(activeStepId) ? activeStepId : null
  );
  const [upsellStepId, setUpsellStepId] = useState<ProjectStepId | null>(null);

  const activeLocked = activeStepId ? isLocked(activeStepId) : false;

  useEffect(() => {
    if (activeStepId && !activeLocked) {
      setOpenStepId(activeStepId);
      setUpsellStepId(null);
    }
  }, [activeStepId, activeLocked]);

  const toggleStep = useCallback(
    (stepId: ProjectStepId) => {
      if (isLocked(stepId)) {
        setUpsellStepId((current) => (current === stepId ? null : stepId));
        setOpenStepId(null);
        return;
      }
      setOpenStepId((current) => (current === stepId ? null : stepId));
      setUpsellStepId(null);
    },
    [isLocked]
  );

  return { openStepId, upsellStepId, toggleStep };
}
